import type { Emotion } from '../types/emotion';

/**
 * "Full charge" — a ~9s cinematic short starring Pixel.
 *
 *  0.0  POWER SURGE   Letterbox bars slide in, the lights go out, Pixel trembles, plates chatter, LEDs blaze.
 *  0.6  WIND-UP       He crouches, curls into a ball and starts spinning faster and faster.
 *  1.2  LAUNCH        Rockets straight up out of frame. Camera shakes. Empty stage.
 *  1.6  FLY-BY        A tiny silhouette barrel-rolls across the far background, left to right.
 *  2.9  DIVE          Drops in from above, straight at the camera — stops inches from the lens.
 *  3.5  CLOSE-UP      Face fills the screen. A beat. He winks. "Hi."
 *  4.5  PULL BACK     Barrel-rolls back to his spot.
 *  5.3  ORBIT         Circles the stage in depth — behind, beside, in front — while the lights come back up.
 *  7.0  FINALE        Crouch, double backflip, stick the landing.
 *  8.2  BOW           Takes a bow. The bars slide away.
 */
export const PARTY_DUR = 9;

export interface PartyPose {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
  /** 1 = side plates tucked in */
  arms: number;
  emotion: Emotion;
  /** 0..1 closes his left eye */
  wink: number;
  look: { x: number; y: number };
  /** Stage lights 0 (dark) .. 1 (normal) */
  lights: number;
  /** Letterbox bars 0 (gone) .. 1 (in) */
  bars: number;
  /** Camera shake strength 0..1 */
  shake: number;
  /** Extra LED glow */
  led: number;
}

const TAU = Math.PI * 2;

// Easing per segment (applies to the segment ending at that key)
const IN_OUT = 0;
const IN = 1;
const OUT = 2;
const LINEAR = 3;

// time, x, y, z, pitch, yaw, roll, arms, ease — rotations end on whole turns so the hand-off is seamless.
// Keys 0.01s apart are hard cuts (both sides off-screen).
const KEYS: number[][] = [
  [0, 0, 0, 0, 0, 0, 0, 0, IN_OUT],
  [0.6, 0, 0.05, 0.2, -0.1, 0, 0, 0, IN_OUT], // surge
  [1.2, 0, -0.35, 0, 0.25, TAU, 0, 1, IN], // crouch, curl, spin-up
  [1.55, 0, 4.5, 0, -0.2, TAU * 3, 0, 1, IN], // LAUNCH — accelerates out of frame
  [1.56, -14, 0.8, -14, 0, TAU * 3, 0, 1, LINEAR], // cut: far left, deep background
  [2.9, 14, 1.4, -14, 0, TAU * 3, -TAU * 2, 1, LINEAR], // tiny rolling fly-by
  [2.91, 0, 5, 1.5, 0.4, TAU * 3, -TAU * 2, 1, LINEAR], // cut: above frame, near
  [3.5, 0, 0.1, 4, -0.15, TAU * 3, -TAU * 2, 0, OUT], // dive at the lens, soft stop
  [4.5, 0, 0.05, 3.9, -0.1, TAU * 3, -TAU * 2, 0, IN_OUT], // close-up hold
  [5.3, 0, 0, 0, 0, TAU * 3, -TAU * 3, 0, IN_OUT], // pull back with a barrel roll
  [7.0, 0, 0, 0, 0, TAU * 3, -TAU * 3, 0, IN_OUT], // (orbit on top of this)
  [7.25, 0, -0.2, 0, 0.2, TAU * 3, -TAU * 3, 1, IN_OUT], // crouch
  [7.9, 0, 1, 0.2, -TAU * 2, TAU * 3, -TAU * 3, 1, IN_OUT], // double backflip
  [8.2, 0, 0, 0, -TAU * 2, TAU * 3, -TAU * 3, 0, OUT], // stick the landing
  [8.5, 0, -0.08, 0.2, -TAU * 2 + 0.45, TAU * 3, -TAU * 3, 0, IN_OUT], // bow
  [PARTY_DUR, 0, 0, 0, -TAU * 2, TAU * 3, -TAU * 3, 0, IN_OUT],
];

const easeInOut = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2);
const easeOut = (p: number) => 1 - (1 - p) ** 3;
const easeIn = (p: number) => p * p * p;
const ease = (mode: number, p: number) =>
  mode === IN ? easeIn(p) : mode === OUT ? easeOut(p) : mode === LINEAR ? p : easeInOut(p);
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
/** 0 → 1 → 0 over [a, b] */
const bump = (t: number, a: number, b: number) => (t < a || t > b ? 0 : Math.sin((Math.PI * (t - a)) / (b - a)));
/** Decaying hit starting at `at` */
const hit = (t: number, at: number, strength: number, decay: number) =>
  t < at ? 0 : strength * Math.exp(-(t - at) * decay);

export function partyPose(t: number): PartyPose {
  const time = Math.max(0, Math.min(PARTY_DUR, t));
  let i = 0;
  while (i < KEYS.length - 2 && time > KEYS[i + 1][0]) i++;
  const a = KEYS[i];
  const b = KEYS[i + 1];
  const e = ease(b[8], clamp01((time - a[0]) / Math.max(b[0] - a[0], 1e-4)));
  const lerp = (k: number) => a[k] + (b[k] - a[k]) * e;

  const pose: PartyPose = {
    x: lerp(1),
    y: lerp(2),
    z: lerp(3),
    pitch: lerp(4),
    yaw: lerp(5),
    roll: lerp(6),
    arms: e < 0.5 ? a[7] : b[7],
    emotion: 'happy',
    wink: 0,
    look: { x: Math.sin(time * 3) * 0.3, y: 0.1 },
    lights: 1,
    bars: 0,
    shake: 0,
    led: 1 + Math.sin(time * 12) * 0.6,
  };

  // --- Power surge: tremble builds, plates chatter, LEDs blaze ---
  if (time < 1.2) {
    const build = clamp01(time / 1.0);
    pose.x += Math.sin(time * 55) * 0.03 * build;
    pose.y += Math.sin(time * 47) * 0.02 * build;
    pose.roll += Math.sin(time * 61) * 0.04 * build;
    if (time > 0.15 && time < 0.6) pose.arms = Math.sin(time * 34) > 0 ? 1 : 0;
    pose.emotion = time < 0.9 ? 'surprised' : 'angry'; // shock → determination
    pose.look = { x: 0, y: 0 };
    pose.led = 1 + build * 2 + (Math.sin(time * 40) > 0 ? 0.6 : 0);
  } else if (time < 1.55) {
    pose.emotion = 'angry';
    pose.led = 3;
  }

  // --- Fly-by: eyes forward in the direction of travel ---
  if (time > 1.56 && time < 2.9) pose.look = { x: 0.9, y: 0 };

  // --- Close-up: looks right at you, tilts his head, winks ---
  if (time > 3.3 && time < 4.6) {
    pose.look = { x: 0, y: 0 };
    pose.emotion = 'neutral'; // round eyes so the wink reads
    pose.roll += bump(time, 3.6, 4.5) * 0.22;
      // A soft, readable wink: eases shut, holds, eases open
    pose.wink = time > 3.85 && time < 4.35 ? Math.sin(Math.PI * clamp01((time - 3.85) / 0.5)) ** 0.6 : 0;
  }

  // --- Orbit: full loop in depth, banking into the turn ---
  if (time > 5.3 && time < 7.0) {
    const ang = TAU * easeInOut((time - 5.3) / 1.7);
    pose.x += Math.sin(ang) * 2;
    pose.z += (Math.cos(ang) - 1) * 2.6;
    pose.yaw += ang;
    pose.roll += -Math.sin(ang) * 0.4;
    pose.look = { x: Math.cos(ang) * 0.6, y: 0 };
  }

  // --- Stage: lights out at the start, back up during the orbit ---
  if (time < 0.6) pose.lights = 1 - 0.85 * easeInOut(time / 0.6);
  else if (time < 5.3) pose.lights = 0.15;
  else if (time < 7.0) pose.lights = 0.15 + 0.85 * easeInOut((time - 5.3) / 1.7);

  if (time < 0.2) pose.bars = 0;
  else if (time < 0.55) pose.bars = easeOut((time - 0.2) / 0.35);
  else if (time < 8.4) pose.bars = 1;
  else pose.bars = 1 - easeInOut(clamp01((time - 8.4) / 0.5));

  pose.shake = hit(time, 1.5, 1, 7) + hit(time, 3.45, 0.5, 9) + hit(time, 8.15, 0.4, 10);

  return pose;
}
